/*
 * <variant-picker> — product-page variant swatch grid.
 *
 * Selection styling (checked / sold-out) is pure CSS (:has) in theme.css.
 * On selection the picker drives a hidden Dawn <variant-selects> "engine"
 * (sets its option values + dispatches change) so Dawn updates price,
 * availability, SKU, inventory, the buy button, the URL and the cart form's
 * [name="id"] natively — the picker doesn't reimplement any of that.
 *
 * It also fires "variant:change" so the theme's custom gallery filter
 * (coin obverse images) and variant-specs / symbols blocks react (bridged in
 * main.js). If no engine is present it falls back to just mirroring [name="id"].
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-variant-picker__grid]');
    if (!this.grid) return;
    if (!this._bound) {
      this.grid.addEventListener('change', (e) => {
        const radio = e.target.closest('.variant-picker__radio');
        if (radio) this.select(radio, true);
      });
      this._bound = true;
    }
    // On load the form id + price are already server-rendered for the checked
    // variant; just make sure [name="id"] matches in case markup drifted.
    const checked = this.grid.querySelector('.variant-picker__radio:checked');
    if (checked) {
      this.mirror(checked);
      this.reveal(checked);
    }
  }

  // Scroll the grid — only the grid, never the page — until the checked tile
  // is visible: when every variant above it is sold out, the server-checked
  // first available variant can sit rows below the grid's scroll viewport.
  // Centered, because the scroll-edge fade would soften a row parked at an edge.
  reveal(radio) {
    const tile = radio.closest('.variant-picker__tile');
    if (!tile) return;
    const g = this.grid.getBoundingClientRect();
    const t = tile.getBoundingClientRect();
    if (t.top >= g.top && t.bottom <= g.bottom) return;
    this.grid.scrollTop += t.top + t.height / 2 - (g.top + g.height / 2);
  }

  cartForm() {
    if (this.dataset.formId) {
      const byId = document.getElementById(this.dataset.formId);
      if (byId) return byId;
    }
    return this.closest('form');
  }

  mirror(radio) {
    const form = this.cartForm();
    const idInput = form && form.querySelector('[name="id"]');
    if (idInput && idInput.value !== radio.value) {
      idInput.value = radio.value;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  engine() {
    if (this._engine !== undefined) return this._engine;
    const id = this.dataset.section ? 'variant-picker__engine-' + this.dataset.section : null;
    this._engine = (id && document.getElementById(id)) || document.querySelector('variant-selects') || null;
    return this._engine;
  }

  // Set the hidden Dawn variant-selects to this variant's options and let Dawn
  // do everything (price / availability / buy / URL / [name="id"]).
  driveEngine(variantId) {
    const engine = this.engine();
    if (!engine) return false;
    const script = engine.querySelector('script[type="application/json"]');
    if (!script) return false;
    let variants;
    try { variants = JSON.parse(script.textContent); } catch (e) { return false; }
    const variant = variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return false;
    const selects = engine.querySelectorAll('select');
    if (!selects.length) return false;
    selects.forEach((sel, i) => {
      const val = variant['option' + (i + 1)];
      if (val != null) sel.value = val;
    });
    engine.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  select(radio, userInitiated) {
    if (radio.disabled) return;

    // Drive Dawn for price/availability/buy/URL/[name="id"]; fall back to a
    // plain [name="id"] mirror + deep link if no engine is present.
    if (!this.driveEngine(radio.value)) {
      this.mirror(radio);
      const url = new URL(location.href);
      url.searchParams.set('variant', radio.value);
      history.replaceState({}, '', url);
    }

    // Custom theme reactions: gallery (coin obverse) + variant-specs / symbols.
    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { id: radio.value, image: radio.dataset.image || '' },
    }));

    if (userInitiated) radio.scrollIntoView({ block: 'nearest' });
  }
}
customElements.define('variant-picker', VariantPicker);
